import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { 
  ChevronLeft, ChevronRight, ClipboardList, Check, AlertCircle,
  Heart, Activity, Target, Brain, CheckCircle2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { Scale, Droplet } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Category icons and labels
const CATEGORY_CONFIG = {
  general_health: { icon: Heart, label: 'Allgemeine Gesundheit', color: 'bg-red-100 text-red-700' },
  vitals: { icon: Activity, label: 'Vitalwerte', color: 'bg-rose-100 text-rose-700' },
  body_data: { icon: Scale, label: 'Körperdaten', color: 'bg-cyan-100 text-cyan-700' },
  pain: { icon: AlertCircle, label: 'Schmerzen', color: 'bg-orange-100 text-orange-700' },
  mobility: { icon: Activity, label: 'Bewegungseinschränkungen', color: 'bg-yellow-100 text-yellow-700' },
  cardiovascular: { icon: Heart, label: 'Herz-Kreislauf', color: 'bg-pink-100 text-pink-700' },
  lifestyle: { icon: Brain, label: 'Lebensstil', color: 'bg-blue-100 text-blue-700' },
  goals: { icon: Target, label: 'Ziele', color: 'bg-green-100 text-green-700' }
};

const PublicAnamnesePage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [template, setTemplate] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState('');
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    fetchAnamneseData();
  }, [token]);

  const fetchAnamneseData = async () => {
    try {
      const response = await axios.get(`${API}/anamnese/public/${token}`);
      setTemplate(response.data.template);
      setCustomerName(response.data.customer_name);
      
      // Group questions by category
      const questions = response.data.template.questions || [];
      const grouped = {};
      questions.forEach(q => {
        if (!grouped[q.category]) {
          grouped[q.category] = [];
        }
        grouped[q.category].push(q);
      });
      
      // Sort questions within each category by order
      Object.keys(grouped).forEach(cat => {
        grouped[cat].sort((a, b) => a.order - b.order);
      });
      
      // Create ordered category list
      const categoryOrder = ['general_health', 'vitals', 'body_data', 'pain', 'mobility', 'cardiovascular', 'lifestyle', 'goals'];
      const orderedCategories = categoryOrder
        .filter(cat => grouped[cat] && grouped[cat].length > 0)
        .map(cat => ({
          key: cat,
          ...CATEGORY_CONFIG[cat],
          questions: grouped[cat]
        }));
      
      setCategories(orderedCategories);
    } catch (err) {
      console.error('Error fetching anamnese:', err);
      setError(err.response?.data?.detail || 'Link ungültig oder abgelaufen');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleMultiChoice = (questionId, option, checked) => {
    setAnswers(prev => {
      const current = prev[questionId] || [];
      if (checked) {
        return { ...prev, [questionId]: [...current, option] };
      } else {
        return { ...prev, [questionId]: current.filter(o => o !== option) };
      }
    });
  };

  const nextStep = () => {
    if (currentStep < categories.length - 1) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const submitAnamnese = async () => {
    // Validate required fields
    const allQuestions = categories.flatMap(cat => cat.questions);
    const missingRequired = [];
    
    for (const question of allQuestions) {
      if (question.required) {
        const answer = answers[question.id];
        const isEmpty = answer === undefined || answer === null || answer === '' || 
          (Array.isArray(answer) && answer.length === 0);
        
        if (isEmpty) {
          missingRequired.push({
            category: question.category,
            question: question.question_text
          });
        }
      }
    }
    
    if (missingRequired.length > 0) {
      // Find the first missing category to navigate to
      const firstMissing = missingRequired[0];
      const categoryIndex = categories.findIndex(cat => cat.key === firstMissing.category);
      if (categoryIndex >= 0) {
        setCurrentStep(categoryIndex);
        window.scrollTo(0, 0);
      }
      
      toast.error(`Bitte alle Pflichtfelder ausfüllen. Es fehlen ${missingRequired.length} Angaben.`);
      return;
    }
    
    try {
      setLoading(true);
      
      // Convert answers to array format
      const answersArray = Object.entries(answers).map(([questionId, answer]) => ({
        question_id: questionId,
        answer: answer
      }));
      
      await axios.post(`${API}/anamnese/public/${token}`, {
        token: token,
        answers: answersArray,
        notes: notes
      });
      
      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting anamnese:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setLoading(false);
    }
  };

  const renderQuestion = (question) => {
    const value = answers[question.id];
    
    switch (question.question_type) {
      case 'yes_no':
        return (
          <RadioGroup
            value={value || ''}
            onValueChange={(val) => handleAnswer(question.id, val)}
            className="flex gap-6"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="ja" id={`${question.id}-ja`} />
              <Label htmlFor={`${question.id}-ja`} className="cursor-pointer text-base">Ja</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="nein" id={`${question.id}-nein`} />
              <Label htmlFor={`${question.id}-nein`} className="cursor-pointer text-base">Nein</Label>
            </div>
          </RadioGroup>
        );
      
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Ihre Antwort..."
            className="text-base"
          />
        );
      
      case 'textarea':
        return (
          <Textarea
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Ihre Antwort..."
            rows={4}
            className="text-base"
          />
        );
      
      case 'number':
        return (
          <Input
            type="number"
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="0"
            className="text-base"
          />
        );
      
      case 'scale':
        return (
          <div className="space-y-4 py-2">
            <div className="flex justify-between text-sm text-gray-500">
              <span>1 (niedrig)</span>
              <span>10 (hoch)</span>
            </div>
            <Slider
              value={[value || 5]}
              onValueChange={([val]) => handleAnswer(question.id, val)}
              min={1}
              max={10}
              step={1}
              className="w-full"
            />
            <div className="text-center text-3xl font-bold text-blue-600">
              {value || 5}
            </div>
          </div>
        );
      
      case 'single_choice':
        return (
          <RadioGroup
            value={value || ''}
            onValueChange={(val) => handleAnswer(question.id, val)}
            className="space-y-3"
          >
            {question.options?.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                <RadioGroupItem value={option} id={`${question.id}-${idx}`} />
                <Label htmlFor={`${question.id}-${idx}`} className="cursor-pointer text-base flex-1">{option}</Label>
              </div>
            ))}
          </RadioGroup>
        );
      
      case 'multi_choice':
        return (
          <div className="space-y-3">
            {question.options?.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                <Checkbox
                  id={`${question.id}-${idx}`}
                  checked={(value || []).includes(option)}
                  onCheckedChange={(checked) => handleMultiChoice(question.id, option, checked)}
                />
                <Label htmlFor={`${question.id}-${idx}`} className="cursor-pointer text-base flex-1">{option}</Label>
              </div>
            ))}
          </div>
        );
      
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            className="text-base"
          />
        );
      
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => handleAnswer(question.id, e.target.value)}
            placeholder="Ihre Antwort..."
            className="text-base"
          />
        );
    }
  };

  if (loading && !template) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-16 w-16 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold mb-2">Link ungültig</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="bg-green-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-green-800">Vielen Dank!</h2>
            <p className="text-gray-600 mb-6">
              Ihre Anamnese wurde erfolgreich gespeichert. Wir freuen uns auf Ihren Besuch!
            </p>
            <p className="text-sm text-gray-500">
              Sie können dieses Fenster jetzt schliessen.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <Card className="mb-6">
          <CardHeader className="text-center">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <ClipboardList className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Gesundheitsfragebogen</CardTitle>
            {customerName && (
              <p className="text-gray-600 mt-2">für {customerName}</p>
            )}
          </CardHeader>
        </Card>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 px-2">
          {categories.map((cat, idx) => {
            const Icon = cat.icon;
            return (
              <div 
                key={cat.key}
                className={`flex flex-col items-center ${idx <= currentStep ? 'text-blue-600' : 'text-gray-400'}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-1 ${
                  idx < currentStep ? 'bg-blue-600 text-white' :
                  idx === currentStep ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-600' :
                  'bg-gray-200'
                }`}>
                  {idx < currentStep ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                </div>
                <span className="text-xs hidden sm:block text-center">{cat.label.split(' ')[0]}</span>
              </div>
            );
          })}
        </div>

        {/* Current Category */}
        {categories.length > 0 && (
          <Card>
            <CardHeader>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${categories[currentStep].color}`}>
                {React.createElement(categories[currentStep].icon, { className: "h-5 w-5" })}
                <span className="font-semibold">{categories[currentStep].label}</span>
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Schritt {currentStep + 1} von {categories.length}
              </p>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Questions */}
              {categories[currentStep].questions.map((question, qIdx) => {
                const answer = answers[question.id];
                const isEmpty = answer === undefined || answer === null || answer === '' || 
                  (Array.isArray(answer) && answer.length === 0);
                const showError = question.required && isEmpty;
                
                return (
                  <div key={question.id} className={`space-y-3 p-4 rounded-lg ${showError ? 'bg-red-50 border border-red-200' : ''}`}>
                    <Label className="text-lg font-medium block">
                      {qIdx + 1}. {question.question_text}
                      {question.required && <span className="text-red-500 ml-1">*</span>}
                    </Label>
                    {question.help_text && (
                      <p className="text-sm text-gray-500">{question.help_text}</p>
                    )}
                    {renderQuestion(question)}
                    {showError && (
                      <p className="text-sm text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        Pflichtfeld - bitte ausfüllen
                      </p>
                    )}
                  </div>
                );
              })}

              {/* Notes on last step */}
              {currentStep === categories.length - 1 && (
                <div className="space-y-3 pt-6 border-t">
                  <Label className="text-lg font-medium">Haben Sie sonst noch Anmerkungen?</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Weitere Informationen, die für uns wichtig sein könnten..."
                    rows={4}
                    className="text-base"
                  />
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between pt-6 border-t">
                <Button
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 0}
                  size="lg"
                >
                  <ChevronLeft className="h-5 w-5 mr-2" />
                  Zurück
                </Button>
                
                {currentStep < categories.length - 1 ? (
                  <Button onClick={nextStep} size="lg">
                    Weiter
                    <ChevronRight className="h-5 w-5 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    onClick={submitAnamnese} 
                    size="lg"
                    className="bg-green-600 hover:bg-green-700"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    ) : (
                      <Check className="h-5 w-5 mr-2" />
                    )}
                    Absenden
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Ihre Daten werden vertraulich behandelt und nur für Ihre persönliche Betreuung verwendet.
        </p>
      </div>
    </div>
  );
};

export default PublicAnamnesePage;
